import React from "react";
import NoData from "dashboards/NoData";
import jsDig from "utils/jsDig";
import allMetrics from "data/allMetrics.js";
import { camelCase, cloneDeep } from "lodash-es";
import MonthlyPpcReport from "./MonthlyPpcReport";
import MonthlySeoReport from "./MonthlySeoReport";
import MonthlyReportIntro from "./MonthlyReportIntro";
import MonthlyReportOutro from "./MonthlyReportOutro";

const allMetricData = allMetrics();

export default function MonthlyReportPdf(props) {
  const goalsPerRow = 5;
  const chartsPerRow = 2;
  const kpiBlocksPerRow = 4;
  const metricsPerTable = 4;
  const goalRowHeight = 119;
  const chartRowHeight = 300;
  const tableRowHeight = 315;
  const kpiBlockRowHeight = 200;
  const keywordRankingRowHeight = 25;
  const checkPpcSections = (name) => {
    return props.ppc_section[name] || props.search_section[name] || props.social_section[name] || props.display_section[name] || props.video_section[name];
  };
  const dashboard = props.dashboard;
  const seo_cc_data = props.seo_section.cc_data;
  const allServices = ["ppc", "search", "social", "display", "video", "seo"];
  let dashboardOrders = [];
  allServices.forEach((service) => {
    const defaultMetrics = jsDig(["metricsByDashboard", service, "defaults", props.client_type], allMetricData);
    if (jsDig([`${service}_section`, "dashboard_order", dashboard], props) && jsDig([`${service}_section`, "dashboard_order", dashboard], props).length > 0) {
      dashboardOrders.push(
        cloneDeep(jsDig([`${service}_section`, "dashboard_order", dashboard], props)).map((obj) => {
          obj["service"] = service;
          return obj;
        })
      )
    } else {
      const sectionList = jsDig(["metricsByDashboard", service, "sections"], allMetricData);
      dashboardOrders.push(
        Object.keys(sectionList).map((sectionName) => {
          let defaultMetricsData = defaultMetrics[sectionName] ? Object.keys(defaultMetrics[sectionName]) : [];
          return {
            name: sectionName,
            service: service,
            enabled: true,
            metrics_data: defaultMetricsData.reduce((sum, item) => {
              sum[item] = "1";
              return sum;
            }, {}),
          };
        })
      );
    }
  });

  // Rearrange goal sections and put them all on the first dashboardOrder
  let goalsAndProjections = [];
  dashboardOrders.forEach((dbOrd, index) => {
    dbOrd.forEach((section, sectionIndex) => {
      if (section.name == "goalsAndProjections") {
        goalsAndProjections.push(section)
        dashboardOrders[index].splice(sectionIndex, 1)
      }
    })
  });
  dashboardOrders.unshift(goalsAndProjections)
  
  // Removing irrelevant sections
  dashboardOrders = dashboardOrders.map((dashboardOrder) => {
    return (dashboardOrder.filter((item) => {
      return (
        (item.name !== "allKpisVsPreviousYear") &&
        (item.name !== "performanceByChannel") &&
        (item.name !== "campaignGroups") &&
        (item.name !== "spendAllocation") &&
        (item.name !== "keywordGroups") &&
        (item.name !== "keywordSummary") &&
        (item.name !== "landingPageBreakdown")
      )
    }))
  });
  
  // Separate ppc & seo dashboard orders for processing
  let seoDashboardOrder = dashboardOrders[dashboardOrders.length - 1];
  dashboardOrders.pop();

  // Removing irrelevant sections
  seoDashboardOrder = seoDashboardOrder.filter((item) => {
    return ((item.name !== "allKpisVsPreviousYear") && (item.name !== "keywordGroups") && (item.name !== "keywordSummary") && (item.name !== "goalsAndProjections") && (item.name !== "landingPageBreakdown"))
  });
  
  // PDFs can't be processed section props.keyword_sort section, we need to get every metric / chart that needs to display and process them sequentially to figure out how much can fit on each page
  let seoPages = [];
  let ppcPages = [];
  let keepingTrack = [];
  let currentPage = 0;

  // Process PPC dashboard order
  dashboardOrders.forEach((dashboardOrder) => {
    for (const [index, section] of dashboardOrder.entries()) {
      console.log(section, "section")
      if (jsDig([`${section.service}_section`, "enabled"], props) || (section.service == "ppc" && checkPpcSections("enabled"))) {
        if (section.enabled) {
          if (section.metrics_data) {
            keepingTrack.push(section);
            if (
              section.name != "goalsAndProjections" &&
              jsDig([keepingTrack.length - 2, "name"], keepingTrack) != "goalsAndProjections" &&
              keepingTrack.length > 1 &&
              jsDig([keepingTrack.length - 2, "service"], keepingTrack) != jsDig([keepingTrack.length - 1, "service"], keepingTrack)
            ) {
              console.log("Service has changed")
              //initialize new page
              currentPage += 1;
              if (!ppcPages[currentPage]) {
                console.log("Initialized new page");
                ppcPages[currentPage] = { metricNames: [], pageHeight: 40 };
              }
            }

            let elementsPerRow = 0;
            let elementHeight = 0;
            if (section.name == "goalsAndProjections") {
              elementsPerRow = goalsPerRow;
              elementHeight = goalRowHeight;
            } else if (section.name == "primaryKpiCharts") {
              elementsPerRow = chartsPerRow;
              elementHeight = chartRowHeight;
            } else if (section.name == "primaryKpiTables") {
              elementsPerRow = metricsPerTable;
              elementHeight = tableRowHeight;
            } else {
              return section.name;
            }

            // Create pages array
            let metricNames = Object.keys(section.metrics_data ? section.metrics_data : {});
            metricNames.forEach((metricName, metricIndex) => {
              if (!ppcPages[currentPage]) {
                ppcPages[currentPage] = { metricNames: [], pageHeight: 0 };
              }
              if (metricIndex == 0) {
                ppcPages[currentPage]["pageHeight"] += 40;
              }
              if (metricIndex % elementsPerRow == 0) {
                let maxPageHeight = 971;
                if (
                  (elementHeight + ppcPages[currentPage]["pageHeight"] > maxPageHeight) || 
                  (section.name !== "goalsAndProjections" && jsDig([keepingTrack.length - 2, "name"], keepingTrack) == "goalsAndProjections")
                ) {
                  //initialize new page
                  currentPage += 1;
                  if (!ppcPages[currentPage]) {
                    console.log("Initialized new page");
                    ppcPages[currentPage] = { metricNames: [], pageHeight: 40 };
                  }
                }

                ppcPages[currentPage]["pageHeight"] += elementHeight;
              }
              ppcPages[currentPage]["metricNames"].push({
                name: metricName,
                section: section.name,
                service: section.service,
              });
              console.log(metricName, "metric", cloneDeep(ppcPages))
            });
          }
        }
      }
    }
  });

  // Process SEO dashboard order
  currentPage = 0;
  for (const [index, section] of seoDashboardOrder.entries()) {
    console.log(section, "section")
    if (jsDig(["seo_section", "enabled"], props)) {
      let metricNames = section.metrics_data && Object.keys(section.metrics_data);
      if (section.enabled) {
        if (section.metrics_data) {
          let elementsPerRow = 0;
          let elementHeight = 0;
          if (section.name == "primaryKpiCharts") {
            elementsPerRow = chartsPerRow;
            elementHeight = chartRowHeight;
          } else if (section.name == "primaryKpiTables") {
            elementsPerRow = metricsPerTable;
            elementHeight = tableRowHeight;
          } else if (section.name == "allKpisVsPreviousYear") {
            elementsPerRow = kpiBlocksPerRow;
            elementHeight = kpiBlockRowHeight;
          } else if (section.name == "backlinks") {
            elementsPerRow = kpiBlocksPerRow;
            elementHeight = kpiBlockRowHeight;
          } else if (section.name == "keywordRankings") {
            elementsPerRow = 1;
            elementHeight = keywordRankingRowHeight;
          } else {
            return section.name;
          }

          if (section.name == "keywordRankings") {
            metricNames = jsDig([dashboard, "keyword_rankings"], seo_cc_data).map((ranking) => ranking["keyword"]);
          }

          metricNames.forEach((metricName, metricIndex) => {
            if (!seoPages[currentPage]) {
              seoPages[currentPage] = { metricNames: [], pageHeight: 0 };
            }

            if (metricIndex == 0) {
              seoPages[currentPage]["pageHeight"] += 40;
            }

            if (metricIndex % elementsPerRow == 0) {
              let maxPageHeight = 971;
              if (elementHeight + seoPages[currentPage]["pageHeight"] > maxPageHeight) {
                currentPage += 1;
                if (!seoPages[currentPage]) {
                  console.log("Initialized new page");
                  seoPages[currentPage] = { metricNames: [], pageHeight: 40 };
                }
                if (section.name == "keywordRankings") {
                  //for table head
                  seoPages[currentPage]["pageHeight"] += 25;
                }
              }

              seoPages[currentPage]["pageHeight"] += elementHeight;
            }
            seoPages[currentPage]["metricNames"].push({
              name: metricName,
              section: section.name,
            });
            console.log(metricName, "metric")
          });
        }
      }
    }
  }

  // Separate goals page from ppc pages
  let goalPage = ppcPages.length > 0 ? [ppcPages.shift()] : []

  console.log(seoPages, "seoPages")
  console.log(ppcPages, "ppcPages")
  console.log(goalPage, "goalPage")

  // Pages start at 1 to account for intro page
  let pageNumber = 1;
  return (
    <>
      <MonthlyReportIntro white_label_service={props.white_label_service} client_name={props.client_name} date_formatted={props.date_formatted} {...props} />
      {goalPage.map((page, index) => {
          pageNumber += 1;
          return (
            <div key={index}>
              <MonthlyPpcReport page={page} pageNumber={pageNumber} dashboard={dashboard} {...props} />
            </div>
          );
        })
      }

      {checkPpcSections("enabled") && jsDig([dashboard, "date"], props.ppc_section.cc_data) && (
        ppcPages.map((page, index) => {
          pageNumber += 1;
          return (
            <div key={index}>
              <MonthlyPpcReport page={page} pageNumber={pageNumber} dashboard={dashboard} {...props} />
            </div>
          );
        })
      )}

      {props.seo_section.enabled && jsDig([dashboard, "date"], seo_cc_data) && (
        seoPages.map((page, index) => {
          pageNumber += 1;
          return (
            <div key={index}>
              <MonthlySeoReport page={page} pageNumber={pageNumber} seo_cc_data={seo_cc_data} dashboard={dashboard} {...props} />
            </div>
          );
        })
      )}
      <MonthlyReportOutro pageNumber={pageNumber + 1} white_label_service={props.white_label_service} />
    </>
  );
}
